/**
 * Shared helpers for creating new workflows — used by both the UI
 * CreateWorkflow component and the ui_project_create_workflow MCP tool.
 * Keeps the initial BPMN template and naming rules in ONE place so the
 * two entry points can never drift.
 */

/** Name rule: starts with a letter, then letters/digits/spaces (min 2 chars). */
export const WORKFLOW_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9 ]+$/;

/** Max length for a workflow name. */
export const WORKFLOW_NAME_MAX_LENGTH = 50;

/** Returns a user-facing error message if invalid, otherwise null. */
export function validateWorkflowName(name: string | undefined | null): string | null {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "Process name is required.";
  if (trimmed.length > WORKFLOW_NAME_MAX_LENGTH) {
    return `Process name must be ${WORKFLOW_NAME_MAX_LENGTH} characters or less.`;
  }
  if (!WORKFLOW_NAME_PATTERN.test(trimmed)) {
    return "Process name is invalid. Must start with a letter and contain only English letters, digits, or spaces (min 2 chars).";
  }
  return null;
}

export interface NamedItem {
  id?: string;
  name?: string;
  workFlowName?: string;
}

export interface NameConflictInput {
  name: string;
  workflows?: NamedItem[];
  screens?: NamedItem[];
  datasources?: NamedItem[];
  /** When editing an existing workflow, allow its own name. */
  excludeWorkflowId?: string;
}

export interface NameConflict {
  kind: "workflow" | "screen" | "datasource";
  conflictingItemName: string;
  conflictingItemId?: string;
}

/** Case-insensitive name collision check across workflows/screens/datasources. */
export function findWorkflowNameConflict(input: NameConflictInput): NameConflict | null {
  const lowered = input.name.toLowerCase();
  const itemName = (item: NamedItem): string => (item.name ?? item.workFlowName ?? "").toLowerCase();

  const wf = (input.workflows ?? []).find((w) => itemName(w) === lowered && w.id !== input.excludeWorkflowId);
  if (wf) {
    return { kind: "workflow", conflictingItemName: wf.name ?? wf.workFlowName ?? "", conflictingItemId: wf.id };
  }

  const ds = (input.datasources ?? []).find((d) => itemName(d) === lowered);
  if (ds) return { kind: "datasource", conflictingItemName: ds.name ?? "", conflictingItemId: ds.id };

  const sc = (input.screens ?? []).find((s) => itemName(s) === lowered);
  if (sc) return { kind: "screen", conflictingItemName: sc.name ?? "", conflictingItemId: sc.id };

  return null;
}

/** The canonical user-facing warning for a name collision — create and rename show the same one. */
export function workflowNameConflictMessage(conflict: NameConflict): string {
  return conflict.kind === "workflow"
    ? "Process name already exists"
    : "Process name can not be equal to the datasource name or screen name";
}

/**
 * Canonical node ids of the default workflow skeleton. These are FIXED literals
 * baked into `buildDefaultWorkflowJson` / `buildDefaultWorkflowXml` (every new
 * workflow ships with exactly these), so callers can reference the placeholder
 * step, start/end events and the default lane WITHOUT guessing or running a LIST
 * first. KOA-17362: the WorkflowBuilder guessed `Activity_1` for the Sample Step
 * (the real id is `Activity_0dyeav8`), which poisoned its whole first script and
 * triggered a 10-turn delete/recreate spiral. `create_workflow` returns these so
 * the agent reuses the real placeholder id. If you change the template's ids,
 * change these too — they must stay in lockstep.
 */
export interface DefaultWorkflowSkeletonIds {
  startEventId: string;
  endEventId: string;
  sampleStepId: string;
  defaultLaneId: string;
}
export const DEFAULT_WORKFLOW_SKELETON_IDS: DefaultWorkflowSkeletonIds = {
  startEventId: "Event_1k47fui",
  endEventId: "Event_0uithyl",
  sampleStepId: "Activity_0dyeav8",
  defaultLaneId: "Lane_0ixyh1n"
};

/**
 * Initial BPMN JSON for a new workflow:
 * StartEvent → UserTask("Sample Step") → EndEvent inside a single
 * "Everyone" lane. The name appears in the description + diagram label.
 * Node ids are the fixed literals in {@link DEFAULT_WORKFLOW_SKELETON_IDS}.
 */
export function buildDefaultWorkflowJson(name: string): string {
  return `{"nodes":{"decisions":[],"events":[{"id":"Event_0uithyl","name":"END","nodeType":4,"eventType":2},{"id":"Event_1k47fui","name":"START","nodeType":4,"eventType":1}],"forwards":[{"id":"Flow_0jdp0dq","nodeType":3,"arrowType":1,"fromNodeId":"Event_1k47fui","toNodeId":"Activity_0dyeav8"},{"id":"Flow_10ukzij","nodeType":3,"arrowType":1,"fromNodeId":"Activity_0dyeav8","toNodeId":"Event_0uithyl"}],"gateways":[],"steps":[{"id":"Activity_0dyeav8","name":"Sample Step","description":"","dueIn":{"dueUnit":3,"dueValue":1},"stepType":1,"nodeType":1,"useLaneAssignment":true,"assignment":{"assignmentId":"Everyone","assignmentType":3},"parameters":[]}],"lanes":[{"id":"Lane_0ixyh1n","name":"Everyone","nodeType":5,"assignment":{"assignmentId":"Everyone","assignmentType":3}}],"description":"${name}","dueIn":{"dueUnit":3,"dueValue":1}}}`;
}

/** Initial BPMN XML pairing with buildDefaultWorkflowJson. */
export function buildDefaultWorkflowXml(name: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?> <definitions xmlns="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:omgdi="http://www.omg.org/spec/DD/20100524/DI" xmlns:omgdc="http://www.omg.org/spec/DD/20100524/DC" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" id="sid-38422fae-e03e-43a3-bef4-bd33b32041b2" targetNamespace="http://bpmn.io/bpmn" exporter="bpmn-js (https://demo.bpmn.io)" exporterVersion="8.8.2">   <collaboration id="Collaboration_1ug2s1b">     <participant id="Participant_19w0ysq" name="${name}" processRef="Process_1" />   </collaboration>   <process id="Process_1"  name="${name}" isExecutable="false">     <laneSet id="LaneSet_1kxliwm">       <lane id="Lane_0ixyh1n" name="Everyone">         <flowNodeRef>Event_0uithyl</flowNodeRef>         <flowNodeRef>Activity_0dyeav8</flowNodeRef>         <flowNodeRef>Event_1k47fui</flowNodeRef>       </lane>     </laneSet>     <endEvent id="Event_0uithyl" name="END">       <incoming>Flow_10ukzij</incoming>     </endEvent>     <userTask id="Activity_0dyeav8" name="Sample Step">       <incoming>Flow_0jdp0dq</incoming>       <outgoing>Flow_10ukzij</outgoing>     </userTask>     <sequenceFlow id="Flow_0jdp0dq" sourceRef="Event_1k47fui" targetRef="Activity_0dyeav8" />     <sequenceFlow id="Flow_10ukzij" sourceRef="Activity_0dyeav8" targetRef="Event_0uithyl" />     <startEvent id="Event_1k47fui" name="START">       <outgoing>Flow_0jdp0dq</outgoing>     </startEvent>   </process>   <bpmndi:BPMNDiagram id="BpmnDiagram_1">     <bpmndi:BPMNPlane id="BpmnPlane_1" bpmnElement="Collaboration_1ug2s1b">       <bpmndi:BPMNShape id="Participant_19w0ysq_di" bpmnElement="Participant_19w0ysq" isHorizontal="true">         <omgdc:Bounds x="130" y="40" width="660" height="385" />       </bpmndi:BPMNShape>       <bpmndi:BPMNShape id="Lane_0ixyh1n_di" bpmnElement="Lane_0ixyh1n" isHorizontal="true">         <omgdc:Bounds x="160" y="40" width="630" height="385" />       </bpmndi:BPMNShape>       <bpmndi:BPMNEdge id="Flow_0jdp0dq_di" bpmnElement="Flow_0jdp0dq">         <omgdi:waypoint x="270" y="173" />         <omgdi:waypoint x="270" y="273" />         <omgdi:waypoint x="415" y="273" />       </bpmndi:BPMNEdge>       <bpmndi:BPMNEdge id="Flow_10ukzij_di" bpmnElement="Flow_10ukzij">         <omgdi:waypoint x="515" y="273" />         <omgdi:waypoint x="685" y="273" />         <omgdi:waypoint x="685" y="173" />       </bpmndi:BPMNEdge>       <bpmndi:BPMNShape id="Event_0uithyl_di" bpmnElement="Event_0uithyl">         <omgdc:Bounds x="667" y="137" width="36" height="36" />         <bpmndi:BPMNLabel>           <omgdc:Bounds x="673" y="113" width="24" height="14" />         </bpmndi:BPMNLabel>       </bpmndi:BPMNShape>       <bpmndi:BPMNShape id="Activity_0dyeav8_di" bpmnElement="Activity_0dyeav8">         <omgdc:Bounds x="415" y="233" width="100" height="80" />       </bpmndi:BPMNShape>       <bpmndi:BPMNShape id="Event_1k47fui_di" bpmnElement="Event_1k47fui">         <omgdc:Bounds x="252" y="137" width="36" height="36" />         <bpmndi:BPMNLabel>           <omgdc:Bounds x="252" y="113" width="36" height="14" />         </bpmndi:BPMNLabel>       </bpmndi:BPMNShape>     </bpmndi:BPMNPlane>   </bpmndi:BPMNDiagram> </definitions> `;
}
